import {
    Group,
    Scene,
    Object3D,
    Vector3,
    Euler,
    PlaneBufferGeometry,
    MeshBasicMaterial,
    Mesh,
    DoubleSide,
    CanvasTexture,
} from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader";

import { IOnlineObject, ObjectType } from "../OnlineObjects";
import { IGameObject } from "../IGameObject";
import { PLAYER_HEIGHT, PLAYER_MODEL_SCALE } from "../Constants";
import { slerpRotation } from "../Tools";

class OtherPlayer implements IGameObject {
    public id: string;

    public model: Object3D | null;

    public position: Vector3;

    public rotation: Euler;

    public velocity: Vector3;

    public type: ObjectType;

    /** Where they are online, quickly interpolate to this position so it looks smooth */
    public targetPosition: Vector3;

    /** Where they are online, quickly interpolate to this rotation so it looks smooth */
    public targetRotation: Euler;

    public name: string;

    constructor(scene: Scene, id: string, name: string) {
        // TODO use passed in loaders.
        const loader = new OBJLoader();
        const mtlLoader = new MTLLoader();
        this.model = null;
        this.type = "player";

        mtlLoader.load(
            `${process.env.ASSET_URL}/models/Knight_Male.mtl`,
            (materials) => {
                materials.preload();
                loader.setMaterials(materials);
                loader.load(
                    `${process.env.ASSET_URL}/models/Knight_Male.obj`,
                    (model: Group) => {
                        scene.add(model);
                        model.scale.multiplyScalar(PLAYER_MODEL_SCALE);
                        this.model = model;
                        this.update();

                        const ctx = document
                            .createElement("canvas")
                            .getContext("2d");

                        if (ctx) {
                            document.body.appendChild(ctx.canvas);
                            ctx.canvas.width = 256;
                            ctx.canvas.height = 256;

                            ctx.fillStyle = "#FFFFFF";
                            ctx.fillRect(
                                0,
                                0,
                                ctx.canvas.width,
                                ctx.canvas.height
                            );

                            ctx.font = "150px Georgia";
                            ctx.fillStyle = "#000000";
                            ctx.scale(0.2, 1);
                            ctx.fillText(this.name, 0, 200);

                            const geo = new PlaneBufferGeometry(2, 0.4, 1, 1);
                            const texture = new CanvasTexture(ctx.canvas);
                            const mat = new MeshBasicMaterial({
                                map: texture,
                            });
                            const plane = new Mesh(geo, mat);
                            plane.position.set(0, 4, 0);

                            model.add(plane);
                        }
                    }
                );
            }
        );
        this.position = new Vector3();
        this.targetPosition = new Vector3();
        this.rotation = new Euler(0, 0, 0, "XZY");
        this.targetRotation = new Euler(0, 0, 0, "XZY");
        this.velocity = new Vector3();

        this.id = id;
        this.name = name;
    }

    public update(): void {
        // TODO should be a renderor pre-render function
        const { position, model, rotation, targetPosition, targetRotation } =
            this;

        const lerper = 0.1;

        position.set(
            position.x + (targetPosition.x - position.x) * lerper,
            position.y + (targetPosition.y - position.y) * lerper,
            position.z + (targetPosition.z - position.z) * lerper
        );

        const newRotation = slerpRotation({ rotation, targetRotation, lerper });

        rotation.set(newRotation.x, newRotation.y, newRotation.z, "XZY");

        if (model) {
            model.position.set(position.x, position.y, position.z);
            model.rotation.set(rotation.x, rotation.y, rotation.z);
            model.rotateOnAxis(new Vector3(0, 1, 0), Math.PI);
        }
    }

    /**
     * Refresh from online. TODO make OtherPlayerOnline? Rethink how online works.
     */
    public refresh(onlineObject: IOnlineObject): void {
        // TODO player height position should always be correct, camera should contain logic to bump up if need be
        this.targetPosition.set(
            onlineObject.position.x,
            onlineObject.position.y - PLAYER_HEIGHT,
            onlineObject.position.z
        );

        this.targetRotation.set(
            onlineObject.rotation.x,
            onlineObject.rotation.y,
            onlineObject.rotation.z
        );
    }
}

export default OtherPlayer;
